import { AxiosResponse } from 'axios'
import api from './api'
import { ApiResponse, BaseFilters } from './api.types'

// Enums correspondant aux schémas backend
export enum OrganizationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending'
}

export enum OrganizationType {
  ENTERPRISE = 'enterprise',
  SME = 'sme',
  STARTUP = 'startup',
  ASSOCIATION = 'association',
  PUBLIC_SECTOR = 'public_sector'
}

export interface Organization {
  _id: string
  name: string
  code: string
  domain?: string
  type: OrganizationType
  status: OrganizationStatus
  email: string
  phone?: string
  website?: string
  address?: string
  city?: string
  postalCode?: string
  country?: string
  taxNumber?: string
  socialSecurityNumber?: string
  currency: string
  language: string
  timezone: string
  maxEmployees: number
  maxUsers: number
  plan: string
  contractExpiryDate?: Date
  adminUserId?: string
  createdAt: Date
  updatedAt: Date
}

export interface CreateOrganizationDto {
  name: string
  code: string
  description?: string
  status: OrganizationStatus
  type: OrganizationType
  contactEmail?: string
  contactPhone?: string
  address?: string
  maxEmployees?: number
  subscriptionStartDate?: string
  subscriptionEndDate?: string
}

export interface UpdateOrganizationDto extends Partial<CreateOrganizationDto> {}

export interface OrganizationStats {
  total: number
  active: number
  inactive: number
  pending: number
  suspended: number
  byType: Record<string, number>
  bySector: Record<string, number>
  bySize: Record<string, number>
  recentActivity: {
    created: number
    updated: number
    deleted: number
  }
}

export interface OrganizationFilters extends BaseFilters {
  status?: OrganizationStatus
  type?: OrganizationType
  search?: string
  city?: string
  country?: string
  plan?: string
  createdAfter?: Date
  createdBefore?: Date
}

class OrganizationsService {
  // Récupérer toutes les organisations
  async getOrganizations(): Promise<AxiosResponse> {
    const response: AxiosResponse = await api.get('/organizations')
    return response.data
  }

  // Récupérer les organisations actives
  async getActiveOrganizations(): Promise<AxiosResponse> {
    const response: AxiosResponse = await api.get('/organizations/active')
    return response.data
  }

  // Récupérer une organisation par ID
  async getOrganizationById(id: string): Promise<ApiResponse<Organization>> {
    const response = await api.get(`/organizations/${id}`)
    return response.data
  }

  // Récupérer une organisation par code
  async getOrganizationByCode(code: string): Promise<ApiResponse<Organization>> {
    const response = await api.get(`/organizations/code/${code}`)
    return response.data
  }

  // Créer une nouvelle organisation
  async createOrganization(organizationData: CreateOrganizationDto): Promise<ApiResponse<Organization>> {
    const response = await api.post('/organizations', organizationData)
    return response.data
  }

  // Mettre à jour une organisation
  async updateOrganization(id: string, organizationData: UpdateOrganizationDto): Promise<ApiResponse<Organization>> {
    const response = await api.patch(`/organizations/${id}`, organizationData)
    return response.data
  }

  // Supprimer une organisation
  async deleteOrganization(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/organizations/${id}`)
    return response.data
  }

  // Récupérer les statistiques des organisations
  async getOrganizationStats(): Promise<ApiResponse<OrganizationStats>> {
    const response = await api.get('/organizations/stats')
    return response.data
  }
}

export const organizationsService = new OrganizationsService()
